import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePoints } from '../context/PointsContext';
import { useAddress } from '../context/AddressContext';
import CheckoutAddress from '../components/CheckoutAddress';
import { Address } from '../types/address';
import { API_URL } from '../services/api';
import './Checkout.css';

const MINIMUM_ORDER_AMOUNT = 15000;

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, totalPrice} = useCart();
  const { points, forceRefresh } = usePoints();
  const { addresses, getDefaultAddress, loading: addressesLoading } = useAddress();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  const [isPartPayment, setIsPartPayment] = useState(false);
  const [partPaymentAmount, setPartPaymentAmount] = useState('');
  const [showPartPaymentInput, setShowPartPaymentInput] = useState(false);
  const [minPartPayment, setMinPartPayment] = useState(0);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [selectedTotalPrice, setSelectedTotalPrice] = useState(0);
  const [pointsToRedeemFromState, setPointsToRedeemFromState] = useState(0);
  const [shouldApplyPoints, setShouldApplyPoints] = useState(true);

  // Overseas shipping states
  const [overseasProducts, setOverseasProducts] = useState<any[]>([]);
  const [shippingMethod, setShippingMethod] = useState<'air' | 'sea' | null>(null);
  const [overseasSurcharge, setOverseasSurcharge] = useState(0);

  // Refresh points when checkout loads
  useEffect(() => {
    forceRefresh();
  }, [forceRefresh]);

  // Load selected items and points preference from navigation state
  useEffect(() => {
    console.log('Location state:', location.state);

    if (location.state && location.state.selectedItems) {
      setSelectedItems(location.state.selectedItems);
      setSelectedTotalPrice(location.state.selectedTotal || 0);
      setPointsToRedeemFromState(location.state.pointsToRedeem || 0);
      setShouldApplyPoints(location.state.applyPoints !== false);

      console.log('Using selected items:', location.state.selectedItems);
      console.log('Apply points preference:', location.state.applyPoints);
      console.log('Points to redeem:', location.state.pointsToRedeem);

    } else {
      // Fallback: use all cart items if no selection (for backward compatibility)
      console.log('No selected items found, using entire cart');
      setSelectedItems(cart);
      setSelectedTotalPrice(totalPrice);
      setShouldApplyPoints(true);
      setPointsToRedeemFromState(points);
    }
  }, [location.state, cart, totalPrice, points]);

  useEffect(() => {
    // Validate that all selected items have proper product IDs
    const invalidItems = selectedItems.filter(item =>
      !item.productId && !item._id && (!item.id || item.id.includes('_color_') || item.id.includes('_size_'))
    );

    if (invalidItems.length > 0) {
      console.error('Invalid items detected:', invalidItems);
      setError('Some items in your cart have invalid product references. Please remove them and add again.');
    } else {
      // Clear error if all items are valid
      setError(null);
    }
  }, [selectedItems]);

  // Load default address on component mount and when addresses change
  useEffect(() => {
    const defaultAddress = getDefaultAddress();
    console.log('Getting default address:', defaultAddress);
    console.log('All addresses:', addresses);

    if (defaultAddress) {
      setSelectedAddress(defaultAddress);
    } else if (addresses.length > 0) {
      // If no default, select the first address
      setSelectedAddress(addresses[0]);
    } else {
      setSelectedAddress(null);
    }
  }, [addresses, getDefaultAddress]);

  // Check for overseas products in SELECTED items (not all cart items)
  useEffect(() => {
    const overseas = selectedItems.filter(item => item.isOverseas === true);
    setOverseasProducts(overseas);

    // Reset shipping method when no overseas products
    if (overseas.length === 0) {
      setShippingMethod(null);
      setOverseasSurcharge(0);
    }
  }, [selectedItems]);

  // Calculate overseas surcharge when shipping method changes
  useEffect(() => {
    if (shippingMethod === 'air') {
      // Air shipping: 55% of item price
      const totalOverseasValue = overseasProducts.reduce((sum, item) => {
        return sum + (item.price * item.quantity);
      }, 0);
      setOverseasSurcharge(totalOverseasValue * 0.55);
    } else {
      setOverseasSurcharge(0);
    }
  }, [shippingMethod, overseasProducts]);

  // Use selected total price for calculations
  const currentTotalPrice = selectedTotalPrice || totalPrice;

  // CORRECTED: Points calculation - 10 points = ₦1
  // Points to redeem from cart (already in points)
  const pointsToRedeem = shouldApplyPoints ? pointsToRedeemFromState : 0;

  // Convert points to Naira discount (10 points = ₦1)
  const pointsDiscount = pointsToRedeem / 10;

  // Include overseas surcharge in total
  const subtotalWithOverseas = currentTotalPrice + overseasSurcharge;
  const discountedTotal = subtotalWithOverseas - pointsDiscount;

  useEffect(() => {
    if (isPartPayment) {
      const minAmount = Math.ceil(discountedTotal * 0.3); // 30% minimum deposit
      setMinPartPayment(minAmount);
      if (!partPaymentAmount || parseFloat(partPaymentAmount) < minAmount) {
        setPartPaymentAmount(minAmount.toString());
      }
    }
  }, [isPartPayment, discountedTotal, partPaymentAmount]);

  // Points earned on actual amount paid (₦1000 = 10 points, so ₦100 = 1 point)
  const rewardPointsToEarn = Math.floor(discountedTotal / 100);

  // Debug logging
  console.log('Points Debug:', {
    currentTotalPrice,
    points,
    shouldApplyPoints,
    pointsToRedeemFromState,
    pointsToRedeem,
    pointsDiscount,
    discountedTotal,
    rewardPointsToEarn
  });

  // Get shipping notification message
  const getShippingNotification = () => {
    if (overseasProducts.length === 0) return null;

    if (shippingMethod === 'air') {
      return {
        title: '✈️ Air Shipping Selected',
        message: 'Your overseas items will be delivered in approximately 14 days.',
        color: '#2196f3'
      };
    } else if (shippingMethod === 'sea') {
      return {
        title: '🚢 Sea Shipping Selected',
        message: 'Your overseas items will be delivered in 45-50 days.',
        color: '#ff9800'
      };
    }
    return null;
  };

  // Handle address change - this will be called when CheckoutAddress component
  // adds/updates/deletes an address. We just need to re-select the default address
  // from the current addresses array (which should already be updated by the context)
  const handleAddressChange = () => {
    console.log('Address changed, reselecting default address...');
    // The addresses array should already be updated by the context
    // Just reselect the default address from the current addresses
    const defaultAddress = getDefaultAddress();
    if (defaultAddress) {
      setSelectedAddress(defaultAddress);
    } else if (addresses.length > 0) {
      setSelectedAddress(addresses[0]);
    } else {
      setSelectedAddress(null);
    }
  };

  const handlePlaceOrder = async () => {
    // Enforce minimum order amount
    if (discountedTotal < MINIMUM_ORDER_AMOUNT) {
      setError(
        `The minimum order amount is ₦${MINIMUM_ORDER_AMOUNT.toLocaleString()}. Please add more items to continue.`
      );
      return;
    }

    if (!selectedAddress) {
      setError('Please select a delivery address');
      return;
    }

    // Validate shipping method for overseas products
    if (overseasProducts.length > 0 && !shippingMethod) {
      setError('Please select a shipping method for overseas items');
      return;
    }

    // Validate selected items
    if (selectedItems.length === 0) {
      setError('No items selected for checkout');
      return;
    }

    const token = localStorage.getItem('token');

    if (!token) {
      setError('Please login to place an order');
      return;
    }

    /*
    * Open the Paystack window immediately from the user's
    * button click.
    *
    * This prevents the browser from treating the popup
    * as a blocked popup after the API requests finish.
    */
    const isNativePlatform = Capacitor.isNativePlatform();
    const paystackWindow = isNativePlatform
      ? null
      : window.open('', '_blank');

    if (!isNativePlatform && !paystackWindow) {
      console.warn(
        '⚠️ Browser blocked the Paystack popup. Current-tab fallback will be used.'
      );
    } else if (!isNativePlatform) {
      console.log(
        '✅ Paystack payment window opened successfully.'
      );

      paystackWindow.document.title =
        'Connecting to Paystack...';

      paystackWindow.document.body.innerHTML = `
        <div style="
          font-family: Arial, sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          margin: 0;
          background: #f5f7fa;
        ">
          <div style="
            text-align: center;
            background: white;
            padding: 32px;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          ">
            <h2 style="
              margin: 0 0 10px;
              color: #1f2937;
            ">
              Connecting to Paystack...
            </h2>

            <p style="
              margin: 0;
              color: #6b7280;
            ">
              Please wait while we prepare your secure payment.
            </p>
          </div>
        </div>
      `;
    }

    setProcessing(true);
    setError(null);

    try {
      // --------------------------------------------------------
      // FORMAT SELECTED ITEMS
      // --------------------------------------------------------

      const formattedItems = selectedItems.map(item => {
        let productId: string | null = null;

        const possibleIds = [
          item.productId,
          item._id,
          item.cartItemId,
          item.id
        ];

        for (const value of possibleIds) {
          if (!value) continue;

          const match = String(value).match(/[a-f0-9]{24}/i);

          if (match) {
            productId = match[0];
            break;
          }
        }

        console.log(
          `✅ Clean productId: ${productId} for item: ${item.name}`
        );

        if (!productId) {
          throw new Error(
            `Invalid product ID for item: ${item.name}`
          );
        }

        let numericSize = null;

        if (item.selectedSize) {
          const sizeMatch =
            String(item.selectedSize).match(/\d+/);

          if (sizeMatch) {
            numericSize = parseInt(
              sizeMatch[0],
              10
            );
          }
        }

        return {
          product: productId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image || '',
          isOverseas: item.isOverseas || false,
          selectedColor: item.selectedColor,
          selectedSize: numericSize
        };
      });

      // --------------------------------------------------------
      // CALCULATE PAYSTACK AMOUNT
      // --------------------------------------------------------

      let finalPaymentAmount = discountedTotal;
      let isPartPaymentOrder = false;
      let depositAmount = 0;

      if (isPartPayment && partPaymentAmount) {
        depositAmount = parseFloat(partPaymentAmount);

        if (!Number.isFinite(depositAmount)) {
          throw new Error(
            'Please enter a valid deposit amount'
          );
        }

        if (depositAmount < minPartPayment) {
          throw new Error(
            `Minimum deposit is ₦${minPartPayment.toLocaleString()}`
          );
        }

        if (depositAmount > discountedTotal) {
          throw new Error(
            'Deposit cannot exceed the total amount'
          );
        }

        finalPaymentAmount = depositAmount;

        isPartPaymentOrder =
          depositAmount < discountedTotal;
      }

      if (finalPaymentAmount <= 0) {
        throw new Error(
          'Payment amount must be greater than zero'
        );
      }

      // --------------------------------------------------------
      // CREATE ORDER DATA
      // --------------------------------------------------------

      const orderData = {
        items: formattedItems,

        subtotal: currentTotalPrice,

        overseasSurcharge: overseasSurcharge,

        shippingMethod:
          overseasProducts.length > 0
            ? shippingMethod
            : null,

        pointsEarned: 0,

        pointsRedeemed: pointsToRedeem,

        pointsDiscount: pointsDiscount,

        total: discountedTotal,

        paymentMethod: 'paystack',

        shippingAddress: {
          fullName: selectedAddress.fullName,
          address: `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.state}`,
          city: selectedAddress.city,
          state: selectedAddress.state,
          postalCode:
            selectedAddress.postalCode || '',
          phone: selectedAddress.phone
        },

        // Part-payment information
        isPartPayment: isPartPaymentOrder,

        partPaymentDeposit:
          isPartPaymentOrder
            ? depositAmount
            : 0,

        remainingBalance:
          isPartPaymentOrder
            ? discountedTotal - depositAmount
            : 0,

        amountToPayNow: finalPaymentAmount
      };

      console.log(
        'Creating Paystack order:',
        {
          total: discountedTotal,
          amountToPayNow: finalPaymentAmount,
          isPartPayment: isPartPaymentOrder,
          paymentMethod: 'paystack'
        }
      );

      // --------------------------------------------------------
      // CREATE ORDER
      // --------------------------------------------------------

      const orderResponse = await fetch(
        `${API_URL}/orders`,
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },

          body: JSON.stringify(orderData)
        }
      );

      const orderDataResponse =
        await orderResponse.json();

      console.log(
        'Create order response:',
        orderDataResponse
      );

      if (
        !orderResponse.ok ||
        !orderDataResponse.success
      ) {
        throw new Error(
          orderDataResponse.error ||
          orderDataResponse.message ||
          'Failed to create order'
        );
      }

      const createdOrder =
        orderDataResponse.order;

      if (!createdOrder?._id) {
        throw new Error(
          'Order was created but no order ID was returned'
        );
      }

      console.log(
        '✅ Order created successfully:',
        createdOrder._id
      );

      // --------------------------------------------------------
      // INITIALIZE PAYSTACK PAYMENT
      // --------------------------------------------------------

      const paymentResponse = await fetch(
        `${API_URL}/payments/paystack/initialize`,
        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },

          body: JSON.stringify({
            orderId: createdOrder._id,
            purpose: isPartPaymentOrder
              ? 'deposit'
              : 'order'
          })
        }
      );

      const paymentData =
        await paymentResponse.json();

      console.log(
        'Paystack initialization response:',
        paymentData
      );

      if (
        !paymentResponse.ok ||
        !paymentData.success
      ) {
        throw new Error(
          paymentData.error ||
          paymentData.message ||
          'Unable to initialize Paystack payment'
        );
      }

      // --------------------------------------------------------
      // GET PAYSTACK CHECKOUT URL
      // --------------------------------------------------------

      const authorizationUrl =
        paymentData.checkoutUrl ||
        paymentData.authorizationUrl ||
        paymentData.data?.authorization_url ||
        paymentData.data?.authorizationUrl ||
        paymentData.data?.checkoutUrl;

      console.log(
        '🔗 Paystack authorization URL:',
        authorizationUrl
      );

      if (!authorizationUrl) {
        throw new Error(
          'Paystack did not return a payment authorization URL'
        );
      }

      // --------------------------------------------------------
      // GET PAYMENT REFERENCE
      // --------------------------------------------------------

      const paymentReference =
        paymentData.payment?.paymentReference ||
        paymentData.payment?.providerReference ||
        paymentData.payment?.paystackReference ||
        paymentData.paymentReference ||
        paymentData.providerReference ||
        paymentData.reference ||
        paymentData.data?.reference;

      console.log(
        '🔑 Paystack payment reference:',
        paymentReference
      );

      if (!paymentReference) {
        throw new Error(
          'Paystack payment reference was not returned by the server'
        );
      }

      // --------------------------------------------------------
      // OPEN PAYSTACK CHECKOUT
      // --------------------------------------------------------

      if (isNativePlatform) {
        console.log('🚀 Opening Paystack using Capacitor Browser...');

        await Browser.open({
          url: authorizationUrl
        });

      } else if (
        paystackWindow &&
        !paystackWindow.closed
      ) {
        console.log(
          '🚀 Opening Paystack checkout in new window...'
        );

        paystackWindow.location.href =
          authorizationUrl;

      } else {
        console.warn(
          '⚠️ Paystack popup was blocked. Opening checkout in the current tab.'
        );

        window.location.href =
          authorizationUrl;

        return;
      }

      // --------------------------------------------------------
      // MONITOR PAYSTACK PAYMENT
      // --------------------------------------------------------

      /*
      * Paystack may remain on its hosted checkout page after
      * a declined test transaction.
      *
      * Therefore, do not depend exclusively on the browser
      * redirect/callback.
      *
      * The original GVS tab periodically asks our backend
      * to verify the Paystack reference.
      *
      * Our backend already:
      *
      *   1. Calls Paystack Verify Transaction
      *   2. Detects successful payments
      *   3. Detects failed/abandoned/reversed payments
      *   4. Settles successful payments
      *   5. Marks unsuccessful payments as failed
      */

      console.log(
        '🔍 Starting Paystack payment monitoring...'
      );

      const pollIntervalMs = 3000;
      const initialGracePeriodMs = 15000;
      const maxPollingTimeMs = 5 * 60 * 1000;

      const pollingStartedAt = Date.now();

      let paymentResolved = false;

      /*
      * Give Paystack time to load and allow the customer
      * to interact with the hosted checkout page.
      */
      await new Promise(resolve =>
        setTimeout(resolve, initialGracePeriodMs)
      );

      while (
        !paymentResolved &&
        Date.now() - pollingStartedAt <
          maxPollingTimeMs
      ) {
        await new Promise(resolve =>
          setTimeout(
            resolve,
            pollIntervalMs
          )
        );

        /*
        * The user may have completed the payment through
        * the Paystack tab while this request was waiting.
        *
        * We still verify against the server rather than
        * trusting the browser window.
        */

        try {
          const verifyResponse =
            await fetch(
              `${API_URL}/payments/paystack/verify/${encodeURIComponent(
                paymentReference
              )}`,
              {
                method: 'GET',

                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );

          const verifyData =
            await verifyResponse.json();

          console.log(
            '🔍 Paystack payment status:',
            verifyData
          );

          /*
          * SUCCESS
          *
          * The backend settlement process has completed.
          */
          if (
            verifyResponse.ok &&
            verifyData.success
          ) {
            const paymentStatus =
              String(
                verifyData.payment?.status ||
                verifyData.status ||
                verifyData.paymentStatus ||
                ''
              ).toLowerCase();

            const orderPaymentStatus =
              String(
                verifyData.order?.paymentStatus ||
                ''
              ).toLowerCase();

            /*
            * If the endpoint reports success without a
            * specific status, we still consider the
            * verification successful because the backend
            * has accepted the transaction.
            */
            if (
              paymentStatus === 'paid' ||
              orderPaymentStatus === 'paid' ||
              verifyData.success === true
            ) {
              paymentResolved = true;

              console.log(
                '✅ Paystack payment verified successfully.'
              );

              /*
              * Stop checking.
              *
              * The payment settlement/webhook process has
              * already handled the order.
              */
              break;
            }
          }

          /*
          * FAILED / ABANDONED / REVERSED
          *
          * The backend verification endpoint handles these
          * statuses and marks the Payment record failed.
          */
          const returnedStatus =
            String(
              verifyData.payment?.status ||
              verifyData.providerStatus ||
              verifyData.status ||
              ''
            ).toLowerCase();

          const terminalFailureStatuses = [
            'failed',
            'abandoned',
            'reversed',
            'cancelled',
            'canceled'
          ];

          if (
            terminalFailureStatuses.includes(
              returnedStatus
            )
          ) {
            paymentResolved = true;

            console.warn(
              `❌ Paystack payment ended with status: ${returnedStatus}`
            );

            setError(
              'Your Paystack payment was not completed. No payment was recorded for this order.'
            );

            break;
          }

          /*
          * If the backend returned an HTTP error, inspect the
          * response but continue polling unless the backend
          * explicitly reports a terminal payment state.
          */
          if (!verifyResponse.ok) {
            console.warn(
              '⚠️ Paystack verification request returned:',
              verifyResponse.status,
              verifyData
            );
          }

        } catch (verificationError) {
          /*
          * A temporary network error should NOT immediately
          * tell the customer that payment failed.
          *
          * Continue polling.
          */
          console.warn(
            '⚠️ Temporary Paystack verification error. Retrying...',
            verificationError
          );
        }
      }

      // --------------------------------------------------------
      // PAYMENT MONITORING TIMEOUT
      // --------------------------------------------------------

      if (!paymentResolved) {
        console.warn(
          '⏱️ Paystack payment monitoring timed out.'
        );

        setError(
          'We could not confirm your Paystack payment yet. Please check My Orders before attempting another payment.'
        );
      }

      /*
      * IMPORTANT:
      *
      * Do NOT remove cart items here.
      *
      * Cart removal should happen only after the backend
      * has successfully settled the payment/order.
      */

    } catch (error: any) {
      console.error(
        'Paystack checkout error:',
        error
      );

      /*
      * If something failed before Paystack opened,
      * close the blank tab so the user is not left
      * with an unnecessary empty window.
      */
      if (
        paystackWindow &&
        !paystackWindow.closed
      ) {
        paystackWindow.close();
      }

      setError(
        error?.message ||
        'Unable to start Paystack payment'
      );

    } finally {
      setProcessing(false);
    }
  };

  const shippingNotification = getShippingNotification();

  // Debug logging
  console.log('Checkout state:', {
    selectedAddress,
    hasAddress: !!selectedAddress,
    addressesCount: addresses.length,
    addresses: addresses,
    overseasProductsCount: overseasProducts.length,
    shippingMethod,
    selectedItemsCount: selectedItems.length,
    selectedTotal: currentTotalPrice,
    canPlaceOrder: !!(selectedAddress && (overseasProducts.length === 0 || shippingMethod) && selectedItems.length > 0)
  });

  if (selectedItems.length === 0) {
    return (
      <div className="checkout-empty">
        <h2>No Items Selected</h2>
        <p>Please select items to checkout from your cart!</p>
        <button onClick={() => navigate('/cart')}>Go to Cart</button>
      </div>
    );
  }

  // Show loading state while addresses are being loaded
  if (addressesLoading) {
    return (
      <div className="checkout-loading">
        <div className="spinner"></div>
        <p>Loading your addresses...</p>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <h2>Checkout ({selectedItems.length} items selected)</h2>

      {error && (
        <div className="checkout-error">
          ❌ {error}
        </div>
      )}

      {/* Show warning if no address is selected */}
      {!selectedAddress && addresses.length === 0 && (
        <div className="address-warning">
          ⚠️ Please add a delivery address to continue with your order.
        </div>
      )}

      <div className="checkout-flow-card">
        <section className="checkout-flow-section checkout-delivery-step">
      {/* Address Section */}
      <div className="address-section">
        <CheckoutAddress
          selectedAddress={selectedAddress}
          onAddressChange={handleAddressChange}
        />
      </div>

      {/* Overseas Products Warning */}
      {overseasProducts.length > 0 && (
        <div className="overseas-notification">
          <div className="overseas-warning">
            <span className="warning-icon">✈️</span>
            <div className="warning-text">
              <strong>Overseas Items Detected</strong>
              <p>Your cart contains products sourced from abroad. These require special shipping arrangements.</p>
            </div>
          </div>

          <div className="overseas-items-list">
            <p><strong>Overseas items in your cart:</strong></p>
            <ul>
              {overseasProducts.map((item, idx) => (
                <li key={idx}>
                  {item.name} x{item.quantity} - ₦{(item.price * item.quantity).toLocaleString()}
                </li>
              ))}
            </ul>
          </div>

          {/* Radio Buttons for Shipping Method */}
          <div className="shipping-method-section">
            <h4>Select Shipping Method</h4>
            <div className="shipping-options">
              <label className={`shipping-option ${shippingMethod === 'air' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="air"
                  checked={shippingMethod === 'air'}
                  onChange={() => setShippingMethod('air')}
                />
                <div className="shipping-option-content">
                  <span className="shipping-icon">✈️</span>
                  <div className="shipping-details">
                    <strong>Air Shipping</strong>
                    <span className="shipping-time">Delivery: 14 days</span>
                    <span className="shipping-cost">Fee: Express rate</span>
                  </div>
                  <span className="shipping-price">
                    +₦{(overseasProducts.reduce((sum, item) => sum + (item.price * item.quantity), 0) * 0.55).toLocaleString()}
                  </span>
                </div>
              </label>

              <label className={`shipping-option ${shippingMethod === 'sea' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="sea"
                  checked={shippingMethod === 'sea'}
                  onChange={() => setShippingMethod('sea')}
                />
                <div className="shipping-option-content">
                  <span className="shipping-icon">🚢</span>
                  <div className="shipping-details">
                    <strong>Sea Shipping</strong>
                    <span className="shipping-time">Delivery: 45-50 days</span>
                    <span className="shipping-cost">Fee: Standard rate</span>
                  </div>
                  <span className="shipping-price">₦0</span>
                </div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Notification */}
      {shippingNotification && (
        <div
          className="shipping-notification"
          style={{ backgroundColor: shippingNotification.color + '20', borderLeftColor: shippingNotification.color }}
        >
          <h4>{shippingNotification.title}</h4>
          <p>{shippingNotification.message}</p>
        </div>
      )}


        </section>

        <section className="checkout-flow-section checkout-summary-step">
      <div className="checkout-summary">
        <h3>Order Summary ({selectedItems.length} items)</h3>
        {selectedItems.map(item => (
          <div key={item.id} className="checkout-item">
            <span>{item.name} x{item.quantity}</span>
            <span>₦{(item.price * item.quantity).toLocaleString()}</span>
          </div>
        ))}

        <div className="summary-row">
          <span>Shipping</span>
          <span className="free-shipping">FREE</span>
        </div>

        {/* Show overseas surcharge if applicable */}
        {overseasSurcharge > 0 && (
          <div className="checkout-overseas-surcharge">
            <span>✈️ Overseas Surcharge (Air)</span>
            <span>+₦{overseasSurcharge.toLocaleString()}</span>
          </div>
        )}

        <div className="checkout-total">
          <div>Subtotal: ₦{currentTotalPrice.toLocaleString()}</div>
          {overseasSurcharge > 0 && (
            <div>Shipping Surcharge: +₦{overseasSurcharge.toLocaleString()}</div>
          )}
          {shouldApplyPoints && pointsToRedeem > 0 && (
            <div>Points Used: -₦{pointsDiscount.toLocaleString()} ({pointsToRedeem.toLocaleString()} points)</div>
          )}
          <div className="grand-total">Total: ₦{discountedTotal.toLocaleString()}</div>
          <div className="points-earn">
            ✨ You'll earn {rewardPointsToEarn.toLocaleString()} Reward Points after payment confirmation!
          </div>
        </div>
      </div>


        </section>

        <section className="checkout-flow-section checkout-payment-step">
      <div className="payment-section">
        <h3>Secure Payment</h3>

        <div className="paystack-payment-option">
          <div className="paystack-payment-icon">
            💳<strong>Pay with Paystack</strong>
          </div>

          <div className="paystack-payment-details">
            <p>
              Secure payment by card, bank transfer,
              USSD, or other Paystack-supported payment
              methods.
            </p>

            <small>
              You will be redirected to Paystack's secure
              payment page to complete your payment.
            </small>
          </div>
        </div>
      </div>


      {/* Part-Payment Option */}
      <div className="part-payment-section">
        <label className="part-payment-checkbox">
          <input
            type="checkbox"
            checked={isPartPayment}
            onChange={(e) => {
              setIsPartPayment(e.target.checked);
              setShowPartPaymentInput(e.target.checked);
              if (!e.target.checked) {
                setPartPaymentAmount('');
              }
            }}
          />
          <span>Make Part Payment (Deposit)</span>
        </label>

        {showPartPaymentInput && (
          <div className="part-payment-input-group">
            <label>Deposit Amount (₦)</label>
            <input
              type="number"
              value={partPaymentAmount}
              onChange={(e) => setPartPaymentAmount(e.target.value)}
              placeholder={`Minimum: ₦${minPartPayment.toLocaleString()}`}
              min={minPartPayment}
              max={discountedTotal}
            />
            <small className="part-payment-info">
              Minimum deposit: ₦{minPartPayment.toLocaleString()} (30% of total)
              <br />
              Remaining balance: ₦{Math.max(0, discountedTotal - parseFloat(partPaymentAmount || '0')).toLocaleString()} to be paid before delivery
            </small>
          </div>
        )}
      </div>

      <button
        className={`place-order-btn ${
          (!selectedAddress) ||
          (overseasProducts.length > 0 && !shippingMethod) ||
          selectedItems.length === 0 ||
          discountedTotal < MINIMUM_ORDER_AMOUNT
            ? 'disabled'
            : ''
        }`}
        onClick={handlePlaceOrder}
        disabled={
          processing ||
          !selectedAddress ||
          (overseasProducts.length > 0 && !shippingMethod) ||
          selectedItems.length === 0 ||
          discountedTotal < MINIMUM_ORDER_AMOUNT
        }
      >
        {processing
          ? 'Connecting to Paystack...'
          : discountedTotal < MINIMUM_ORDER_AMOUNT
            ? `Minimum Order: ₦${MINIMUM_ORDER_AMOUNT.toLocaleString()}`
            : `Pay with Paystack • ₦${
                (
                  isPartPayment && partPaymentAmount
                    ? parseFloat(partPaymentAmount)
                    : discountedTotal
                ).toLocaleString()
              }`
        }
      </button>

        </section>
      </div>
    </div>
  );
};

export default Checkout;
